import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useListStaff, useCreateStaff, useUpdateStaff, useDeleteStaff, getListStaffQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2, Plus, Edit, Trash2, Mail, UserPlus, Check, Copy, Users, Shield, KeyRound, UserX, UserCheck } from "lucide-react";

const ALL_PERMISSIONS: { key: string; label: string; group: string }[] = [
  { key: "dashboard", label: "Dashboard", group: "Core" },
  { key: "pos", label: "POS / New Sale", group: "Core" },
  { key: "price_checker", label: "Price Checker", group: "Core" },
  { key: "sales", label: "Sales History", group: "Core" },
  { key: "inventory", label: "Inventory", group: "Products" },
  { key: "brands", label: "Brands", group: "Products" },
  { key: "purchases", label: "Purchases", group: "Products" },
  { key: "returns", label: "Returns", group: "Products" },
  { key: "customers", label: "Customers", group: "People" },
  { key: "suppliers", label: "Suppliers", group: "People" },
  { key: "staff", label: "Staff Management", group: "People" },
  { key: "attendance", label: "Attendance", group: "People" },
  { key: "expenses", label: "Expenses", group: "Finance" },
  { key: "accounting", label: "Accounting & Reports", group: "Finance" },
  { key: "school_fees", label: "Student Fees", group: "School" },
  { key: "payment_hub", label: "Payment Hub", group: "Finance" },
  { key: "reports", label: "Reports", group: "Finance" },
  { key: "sms", label: "Bulk SMS", group: "Tools" },
  { key: "checklists", label: "Checklists", group: "Tools" },
  { key: "settings", label: "Settings", group: "Tools" },
];

const PERM_GROUPS = [...new Set(ALL_PERMISSIONS.map(p => p.group))];

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  phone: z.string().optional(),
  role: z.string().min(1, "Role is required"),
    isActive: z.boolean(),
});

const inviteSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Email is required"),
  role: z.enum(["admin", "manager", "staff"]),
});

// ---- Permissions Editor Dialog ----
interface StoreUser {
  id: number;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  permissions: string[];
  rawPermissions: string | null;
}

function PermissionsDialog({ user, onClose }: { user: StoreUser; onClose: () => void }) {
  const [selected, setSelected] = useState<string[]>(user.permissions);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const toggle = (key: string) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectAll = () => setSelected(ALL_PERMISSIONS.map(p => p.key));
  const clearAll = () => setSelected([]);

  const handleSave = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const res = await fetch(`${BASE}/api/auth/store-users/${user.id}/permissions`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ permissions: selected }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Failed to save"); return; }
      setSaved(true);
      setTimeout(() => { onClose(); }, 800);
    } catch { setError("Network error. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b">
        <div className="flex-1">
          <p className="font-medium">{user.name}</p>
          <p className="text-sm text-muted-foreground">{user.email} · <span className="capitalize">{user.role}</span></p>
        </div>
        <div className="flex gap-1.5">
          <Button variant="ghost" size="sm" onClick={selectAll} className="h-7 text-xs">All</Button>
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">None</Button>
        </div>
      </div>

       <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1" aria-label="Permission groups">
        {PERM_GROUPS.map(group => (
          <fieldset key={group} className="rounded-lg border p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {group} ({ALL_PERMISSIONS.filter(p => p.group === group && selected.includes(p.key)).length}/{ALL_PERMISSIONS.filter(p => p.group === group).length})
            </legend>
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {ALL_PERMISSIONS.filter(p => p.group === group).map(perm => (
                <label key={perm.key} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-2 py-1.5">
                  <Checkbox
                    aria-label={perm.label}
                    checked={selected.includes(perm.key)}
                    onCheckedChange={() => toggle(perm.key)}
                  />
                  <span className="text-sm">{perm.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>

       <p className="sr-only" aria-live="polite">{error || (saved ? "Permissions saved." : `${selected.length} of ${ALL_PERMISSIONS.length} permissions selected.`)}</p>
       {error && <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg" role="alert">{error}</p>}

      <div className="flex gap-2 pt-2 border-t">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
         <Button onClick={handleSave} className="flex-1 gap-2" disabled={saving || saved || JSON.stringify([...selected].sort()) === JSON.stringify([...user.permissions].sort())}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4 text-emerald-500" /> : <Shield className="w-4 h-4" />}
          {saved ? "Saved!" : "Save Permissions"}
        </Button>
      </div>
    </div>
  );
}

// ---- User Accounts & Permissions Section ----
function UserAccountsSection() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin";
  const [users, setUsers] = useState<StoreUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<StoreUser | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const { toast } = useToast();

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${BASE}/api/auth/store-users`, { credentials: "include" });
      if (res.ok) setUsers(await res.json());
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (isAdmin) fetchUsers();
    else setLoading(false);
  }, [isAdmin]);

  const handleDeleteUser = async (u: StoreUser) => {
    if (!confirm(`Remove ${u.name}'s login account? They will no longer be able to sign in.`)) return;
    setDeletingId(u.id);
    try {
      const res = await fetch(`${BASE}/api/auth/store-users/${u.id}`, { method: "DELETE", credentials: "include" });
      const data = await res.json();
      if (!res.ok) { toast({ title: data.error || "Could not delete user", variant: "destructive" }); return; }
      toast({ title: `${u.name}'s account removed` });
      fetchUsers();
    } catch {
      toast({ title: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2"><KeyRound className="w-5 h-5" /> User Accounts & Permissions</h2>
        <p className="text-sm text-muted-foreground mt-1">Control which modules each user can access.</p>
      </div>

      <div className="border rounded-md overflow-hidden">
        <div className="overflow-y-auto max-h-[600px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No user accounts yet. Invite staff to create accounts.</TableCell></TableRow>
            ) : users.map(u => (
              <TableRow key={u.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={u.role === "admin" ? "default" : "secondary"} className="capitalize">{u.role}</Badge>
                </TableCell>
                <TableCell>
                  {u.role === "admin" || u.role === "superadmin" ? (
                    <span className="text-xs text-muted-foreground italic">Full access (admin)</span>
                  ) : (
                    <span className="text-sm">{u.permissions.length} of {ALL_PERMISSIONS.length} modules</span>
                  )}
                </TableCell>
                <TableCell>
                  {u.role !== "admin" && u.role !== "superadmin" && (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => setEditingUser(u)}>
                        <Shield className="w-3.5 h-3.5" /> Permissions
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        title="Delete account"
                        disabled={deletingId === u.id}
                        onClick={() => handleDeleteUser(u)}
                      >
                        {deletingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      <Dialog open={!!editingUser} onOpenChange={o => { if (!o) { setEditingUser(null); fetchUsers(); } }}>
         <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Edit Permissions</DialogTitle>
          </DialogHeader>
          {editingUser && <PermissionsDialog user={editingUser} onClose={() => { setEditingUser(null); fetchUsers(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Invite Staff Modal ----
function InviteModal({ onClose }: { onClose: () => void }) {
  const { user, store } = useAuth();
  const { activeBranchId } = useBranch();
  const [inviteResult, setInviteResult] = useState<{ name: string; email: string; tempPassword: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [copied, setCopied] = useState(false);

  const form = useForm<z.infer<typeof inviteSchema>>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { name: "", email: "", role: "staff" },
  });

  const handleInvite = async (data: z.infer<typeof inviteSchema>) => {
    setLoading(true); setError(""); setUpgradeRequired(false);
    try {
      const res = await fetch(`${BASE}/api/auth/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...data, branchId: activeBranchId ?? undefined }),
      });
      let result: any = {};
      try { result = await res.json(); } catch {}
      if (!res.ok) {
        setError(result.error || `Something went wrong (${res.status}). Please try again.`);
        setUpgradeRequired(result.upgrade === true);
        return;
      }
      setInviteResult(result);
    } catch { setError("Could not reach the server. Check your connection and try again."); }
    finally { setLoading(false); }
  };

  const loginUrl = window.location.origin + (import.meta.env.BASE_URL || "/");

  const buildInstructions = (r: { name: string; email: string; tempPassword: string }) =>
    `Hi ${r.name},\n\nYou've been added to *${store?.name || "our store"}* on RetailPulse.\n\nHere's how to sign in:\n\n1. Open this link: ${loginUrl}\n2. Enter your email: ${r.email}\n3. Enter your password: ${r.tempPassword}\n\nOnce you're in, you can change your password from your account settings.\n\nWelcome to the team! 🎉`;

  const copyCredentials = () => {
    if (!inviteResult) return;
    navigator.clipboard.writeText(buildInstructions(inviteResult));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (inviteResult) {
    return (
      <div className="space-y-4 py-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
            <Check className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-bold">Invitation Created!</h3>
            <p className="text-sm text-muted-foreground">Send these sign-in details to <strong>{inviteResult.name}</strong></p>
          </div>
        </div>
        <div className="bg-muted rounded-xl p-4 text-sm space-y-2">
          <div className="flex justify-between items-center gap-2">
            <span className="text-muted-foreground shrink-0">Store:</span>
            <span className="font-medium text-right">{store?.name}</span>
          </div>
          <div className="border-t my-1" />
          <div className="flex justify-between items-center gap-2">
            <span className="text-muted-foreground shrink-0">Email:</span>
            <span className="font-medium text-right break-all">{inviteResult.email}</span>
          </div>
          <div className="flex justify-between items-center gap-2">
            <span className="text-muted-foreground shrink-0">Password:</span>
            <span className="font-mono font-bold text-primary">{inviteResult.tempPassword}</span>
          </div>
          <div className="border-t my-1" />
          <div className="flex justify-between items-center gap-2">
            <span className="text-muted-foreground shrink-0">Login link:</span>
            <span className="text-xs text-right break-all text-muted-foreground">{loginUrl}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Tip: tap "Copy Instructions" and paste into WhatsApp or SMS to send to them.</p>
        <Button onClick={copyCredentials} variant="outline" className="w-full gap-2">
          {copied ? <><Check className="w-4 h-4 text-emerald-500" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Sign-In Instructions</>}
        </Button>
        <Button onClick={onClose} className="w-full">Done</Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleInvite)} className="space-y-4">
        <FormField control={form.control} name="name" render={({ field }) => (
          <FormItem><FormLabel>Full Name</FormLabel><FormControl><Input {...field} placeholder="Amaka Johnson" /></FormControl><FormMessage /></FormItem>
        )} />
        <FormField control={form.control} name="email" render={({ field }) => (
          <FormItem><FormLabel>Email Address</FormLabel><FormControl><Input type="email" {...field} placeholder="amaka@yourstore.com" /></FormControl><FormMessage /></FormItem>
        )} />
        <FormField control={form.control} name="role" render={({ field }) => (
          <FormItem><FormLabel>Role</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )} />
        {error && (
          <div className="space-y-2">
            <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>
            {upgradeRequired && (
              <Button type="button" variant="outline" className="w-full" onClick={() => { window.location.href = `${BASE}/subscribe`; }}>
                View plans
              </Button>
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground">A temporary password will be generated. Share it securely with the new staff member.</p>
        <Button type="submit" className="w-full gap-2" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          Send Invitation
        </Button>
      </form>
    </Form>
  );
}

// ---- Main Staff Page ----
export default function Staff() {
  const queryClient = useQueryClient();
  const { store, user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const { toast } = useToast();
  const { data: staffList, isLoading } = useListStaff();
  const createStaff = useCreateStaff();
  const updateStaff = useUpdateStaff();
  const deleteStaff = useDeleteStaff();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [resetResult, setResetResult] = useState<{ name: string; email: string; tempPassword: string } | null>(null);
  const [resetting, setResetting] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin && window.location.search.includes("add=1")) setIsCreateOpen(true);
  }, [isAdmin]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", phone: "", role: "Cashier", isActive: true },
  });

  const onSubmit = (data: z.infer<typeof schema>) => {
    setSubmitError(null);
    const payload = { ...data, email: data.email || undefined, phone: data.phone || undefined };
    if (editingId !== null) {
      updateStaff.mutate({ id: editingId, data: payload }, {
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: getListStaffQueryKey() }); setEditingId(null); form.reset(); setSubmitError(null); },
        onError: (err: any) => setSubmitError(err?.message || "Failed to update staff member. Please try again."),
      });
    } else {
      createStaff.mutate({ data: payload }, {
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: getListStaffQueryKey() }); setIsCreateOpen(false); form.reset(); setSubmitError(null); },
        onError: (err: any) => setSubmitError(err?.message || "Failed to add staff member. Please try again."),
      });
    }
  };

  const handleEdit = (s: any) => {
    setEditingId(s.id);
    form.reset({ name: s.name, email: s.email || "", phone: s.phone || "", role: s.role, isActive: s.isActive });
  };

  const handleResetPassword = async (s: any) => {
    if (!confirm(`Reset password for ${s.name}? They will need to use a new temporary password to sign in.`)) return;
    setResetting(s.id);
    try {
      const res = await fetch(`${BASE}/api/auth/reset-staff-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userId: s.accountUserId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setResetResult({ name: data.name, email: data.email, tempPassword: data.tempPassword });
    } catch (err: any) {
      alert(err.message || "Could not reset password");
    } finally {
      setResetting(null);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm("Remove this staff member?")) {
      deleteStaff.mutate({ id }, {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getListStaffQueryKey() }),
        onError: () => toast({ title: "Failed to remove staff member", variant: "destructive" }),
      });
    }
  };

  const staffFormJsx = (isEdit: boolean) => (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField control={form.control} name="name" render={({ field }) => (
          <FormItem><FormLabel>Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
        )} />
        <div className="grid grid-cols-2 gap-4">
          <FormField control={form.control} name="email" render={({ field }) => (
            <FormItem><FormLabel>Email (optional)</FormLabel><FormControl><Input type="email" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
          <FormField control={form.control} name="phone" render={({ field }) => (
            <FormItem><FormLabel>Phone (optional)</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
          )} />
        </div>
        <FormField control={form.control} name="role" render={({ field }) => (
          <FormItem><FormLabel>Role</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
              <SelectContent>
                {["Cashier", "Supervisor", "Manager", "Accountant", "Driver", "Security", "Other"].map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )} />
        <FormField control={form.control} name="isActive" render={({ field }) => (
          <FormItem className="flex items-center justify-between rounded-lg border p-3 shadow-sm">
            <FormLabel className="font-normal">Active</FormLabel>
            <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
          </FormItem>
        )} />
        {submitError && (
          <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{submitError}</p>
        )}
        <Button type="submit" className="w-full" disabled={isEdit ? updateStaff.isPending : createStaff.isPending}>
          {(isEdit ? updateStaff.isPending : createStaff.isPending)
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isEdit ? "Saving..." : "Adding..."}</>
            : isEdit ? "Update Staff" : "Add Staff Member"}
        </Button>
      </form>
    </Form>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Staff</h1>
          <p className="text-muted-foreground mt-1">Manage your team members and access.</p>
        </div>
        {isAdmin && <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsInviteOpen(true)} className="gap-2">
            <Mail className="w-4 h-4" /> Invite Staff
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={(o) => { setIsCreateOpen(o); if (!o) setSubmitError(null); }}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingId(null); form.reset({ name: "", email: "", phone: "", role: "Cashier", isActive: true }); }}>
                <Plus className="w-4 h-4 mr-2" /> Add Staff
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Staff Member</DialogTitle></DialogHeader>
              {staffFormJsx(false)}
            </DialogContent>
          </Dialog>
        </div>}
      </div>

      <Dialog open={!!editingId} onOpenChange={o => { if (!o) { setEditingId(null); setSubmitError(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Staff Member</DialogTitle></DialogHeader>
          {staffFormJsx(true)}
        </DialogContent>
      </Dialog>

      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" /> Invite Staff Member</DialogTitle>
          </DialogHeader>
          <InviteModal onClose={() => setIsInviteOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Reset Password Result */}
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-500" /> Password Reset</DialogTitle>
          </DialogHeader>
          {resetResult && (() => {
            const loginUrl = window.location.origin + (import.meta.env.BASE_URL || "/");
            const instructions = `Hi ${resetResult.name},\n\nYour password for *${store?.name || "our store"}* on RetailPulse has been reset.\n\nHere's how to sign in:\n\n1. Open this link: ${loginUrl}\n2. Enter your email: ${resetResult.email}\n3. Enter your new password: ${resetResult.tempPassword}\n\nPlease change your password after logging in.`;
            return (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Send these updated sign-in details to <strong>{resetResult.name}</strong>. Their old password no longer works.</p>
                <div className="rounded-lg border bg-muted/40 p-4 space-y-2 text-sm">
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-muted-foreground shrink-0">Store:</span>
                    <span className="font-medium text-right">{store?.name}</span>
                  </div>
                  <div className="border-t my-1" />
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-muted-foreground shrink-0">Email:</span>
                    <span className="font-medium text-right break-all">{resetResult.email}</span>
                  </div>
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-muted-foreground shrink-0">New Password:</span>
                    <span className="font-mono font-bold text-primary">{resetResult.tempPassword}</span>
                  </div>
                  <div className="border-t my-1" />
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-muted-foreground shrink-0">Login link:</span>
                    <span className="text-xs text-right break-all text-muted-foreground">{loginUrl}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Tap "Copy Instructions" and paste into WhatsApp or SMS.</p>
                <div className="flex gap-2">
                  <Button className="flex-1 gap-2" variant="outline" onClick={() => navigator.clipboard.writeText(instructions)}>
                    <Copy className="w-4 h-4" /> Copy Instructions
                  </Button>
                  <Button className="flex-1" onClick={() => setResetResult(null)}>Done</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <div className="border rounded-md overflow-hidden">
        <div className="overflow-y-auto max-h-[600px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Status</TableHead>
              {isAdmin && <TableHead className="w-[100px]">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
            ) : (staffList as any[])?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-16">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Users className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                    <div>
                      <p className="font-semibold">No team members yet</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Add staff so they can process sales and you can track performance.</p>
                    </div>
                    {isAdmin && <Button size="sm" className="gap-2 mt-1" onClick={() => setIsCreateOpen(true)}>
                      <UserPlus className="w-4 h-4" /> Add First Team Member
                    </Button>}
                    {isAdmin && <p className="text-xs text-muted-foreground/60">Each staff member gets their own account for the POS.</p>}
                  </div>
                </TableCell>
              </TableRow>
            ) : (staffList as any[])?.map((s: any) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell><Badge variant="secondary">{s.role}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{s.email || s.phone || "—"}</TableCell>
                <TableCell>
                  <Badge variant={s.isActive ? "default" : "outline"} className={!s.isActive ? "text-muted-foreground" : ""}>{s.isActive ? "Active" : "Left"}</Badge>
                </TableCell>
                {isAdmin && <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(s)} title="Edit"><Edit className="w-4 h-4" /></Button>
                    {s.accountUserId && (
                      <Button variant="ghost" size="icon" title="Reset Password" disabled={resetting === s.id} onClick={() => handleResetPassword(s)}>
                        {resetting === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4 text-amber-500" />}
                      </Button>
                    )}
                    {s.isActive ? (
                      <Button
                        variant="ghost" size="icon"
                        title="Mark as Left (deactivate)"
                        onClick={() => {
                          if (confirm(`Mark ${s.name} as left? They will no longer be able to log in.`)) {
                            updateStaff.mutate({ id: s.id, data: { isActive: false } }, {
                              onSuccess: () => queryClient.invalidateQueries({ queryKey: getListStaffQueryKey() })
                            });
                          }
                        }}
                      >
                        <UserX className="w-4 h-4 text-amber-600" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost" size="icon"
                        title="Reactivate"
                        onClick={() => {
                          updateStaff.mutate({ id: s.id, data: { isActive: true } }, {
                            onSuccess: () => queryClient.invalidateQueries({ queryKey: getListStaffQueryKey() })
                          });
                        }}
                      >
                        <UserCheck className="w-4 h-4 text-green-600" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="text-destructive" title="Delete" onClick={() => handleDelete(s.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      <div className="border-t pt-6">
        <UserAccountsSection />
      </div>
    </div>
  );
}
